import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import {
  captureUTMAttributionPostHog,
  getUserRegionForTracking,
  startPostHogGeoRefresh,
  trackPageViewPostHog,
  trackScrollDepthPostHog,
  trackTimeOnPagePostHog,
  waitForPostHogGeoEnrichment,
} from "@/lib/posthogAnalytics";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Tracks pageviews and route_change for PostHog.
 * Also handles: scroll depth (25/50/75/90/100%), time on page,
 * UTM attribution, and route slug enrichment.
 */

/** Paths where PostHog is fully disabled (e.g. fullscreen reels or hero-image page) */
const POSTHOG_DISABLED_PATHS = ["/reels", "/imagenes"];

const SCROLL_THRESHOLDS = [25, 50, 75, 90, 100];

/** Extract slug info from dynamic route patterns */
function extractSlug(pathname: string): { route_type: string; slug?: string } | null {
  const vipMatch = pathname.match(/^\/vip\/([^/]+)/);
  if (vipMatch) return { route_type: "vip", slug: vipMatch[1] };

  const pMatch = pathname.match(/^\/p\/([^/]+)/);
  if (pMatch) return { route_type: "product", slug: pMatch[1] };

  const buyMatch = pathname.match(/^\/buy\/([^/]+)/);
  if (buyMatch) return { route_type: "direct_checkout", slug: buyMatch[1] };

  const reelMatch = pathname.match(/^\/reel\/([^/]+)/);
  if (reelMatch) return { route_type: "reel", slug: reelMatch[1] };

  const giftMatch = pathname.match(/^\/gift\/([^/]+)/);
  if (giftMatch) return { route_type: "gift", slug: giftMatch[1] };

  return null;
}

export function PostHogPageview() {
  const location = useLocation();
  const { templateName } = useCurrentTemplate();
  const isHome = location.pathname === "/";
  const isPostHogDisabled = POSTHOG_DISABLED_PATHS.includes(location.pathname);

  // Refs so cleanup closures always see current values
  const pageEnterTime = useRef<number>(Date.now());
  const scrollDepthReached = useRef<Set<number>>(new Set());
  const disabledRef = useRef<boolean>(isPostHogDisabled);
  disabledRef.current = isPostHogDisabled;

  // ── Scroll depth tracking (rAF-throttled) ───────────────────────────────
  useEffect(() => {
    if (isPostHogDisabled) return;

    scrollDepthReached.current = new Set();
    let rafId: number | null = null;

    const checkScroll = () => {
      rafId = null;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const pct = Math.round((scrollTop / docHeight) * 100);
      for (const threshold of SCROLL_THRESHOLDS) {
        if (pct >= threshold && !scrollDepthReached.current.has(threshold)) {
          scrollDepthReached.current.add(threshold);
          trackScrollDepthPostHog(threshold);
        }
      }
    };

    const handleScroll = () => {
      if (rafId === null) rafId = requestAnimationFrame(checkScroll);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [location.pathname, isPostHogDisabled]);

  // ── Time on page + visibilitychange ─────────────────────────────────────
  // - On route change: fire time_on_page in cleanup
  // - On tab hide: fire page_hidden with partial time (user may not come back)
  useEffect(() => {
    pageEnterTime.current = Date.now();

    const handleVisibility = () => {
      if (disabledRef.current || typeof window === "undefined" || !window.posthog) return;
      const seconds = Math.round((Date.now() - pageEnterTime.current) / 1000);

      if (document.visibilityState === "hidden") {
        window.posthog.capture("page_hidden", {
          page_path: location.pathname,
          time_active_seconds: seconds,
          timestamp: new Date().toISOString(),
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (disabledRef.current) return;
      const seconds = Math.round((Date.now() - pageEnterTime.current) / 1000);
      if (seconds >= 2) trackTimeOnPagePostHog(seconds);
    };
  }, [location.pathname]);

  // ── Pageview + route_change + UTM attribution ────────────────────────────
  useEffect(() => {
    if (isPostHogDisabled) return;

    let cancelled = false;
    // Async IPLocate enrichment: el primer $pageview espera (máx 400ms) para
    // salir con flags VPN/geo cuando el cache está caliente.
    startPostHogGeoRefresh();

    const template = isHome ? templateName : null;

    void (async () => {
      await waitForPostHogGeoEnrichment();
      if (cancelled) return;
      trackPageViewPostHog(location.pathname, document.title, template);

      // Fire our custom utm_attribution event when UTMs are present.
      // PostHog JS already sets utm_* person properties automatically.
      captureUTMAttributionPostHog(location.search, location.pathname);

      if (typeof window !== "undefined" && window.posthog) {
        const slugInfo = extractSlug(location.pathname);
        const userRegion = getUserRegionForTracking();

        const routePayload: Record<string, unknown> = {
          // Navigation context (not auto-captured by PostHog)
          from_path: document.referrer
            ? (() => {
                try { return new URL(document.referrer).pathname; } catch { return null; }
              })()
            : null,
          to_path: location.pathname,
          hash: location.hash,
          page_title: document.title,
          timestamp: new Date().toISOString(),
          // App-level custom properties
          user_region: userRegion,
          test_country: localStorage.getItem("test_country"),
          // $set person properties inline — updates profile on each navigation
          $set: {
            user_region: userRegion,
            test_country: localStorage.getItem("test_country") || null,
          },
        };

        if (template) routePayload.template = template;
        if (slugInfo) {
          routePayload.route_type = slugInfo.route_type;
          routePayload.slug = slugInfo.slug;
        }

        window.posthog.capture("route_change", routePayload);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Omit templateName to avoid duplicate pageview when template settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, location.hash, isHome, isPostHogDisabled]);

  // ── template_view on "/" ─────────────────────────────────────────────────
  useEffect(() => {
    if (
      isPostHogDisabled ||
      !isHome ||
      !templateName ||
      typeof window === "undefined" ||
      !window.posthog
    )
      return;

    window.posthog.capture("template_view", {
      // Doble naming por consistency: otros events del codebase usan
      // `template_type` (base_template_ui_click, dual_cta_*, etc.) mientras
      // template_view usaba `template`. Ahora ambos. Reportes admin pueden
      // filtrar por `template_type` consistentemente.
      template: templateName,
      template_type: templateName,
      path: location.pathname,
      timestamp: new Date().toISOString(),
      user_region: getUserRegionForTracking(),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHome, templateName, location.pathname]);

  return null;
}
